import React, { FC, ReactElement } from "react";

import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Tooltip from "@mui/material/Tooltip";

import { ReduxTextField } from "../../../components/Redux/TextField";
import SettingSection from "../../../components/SettingSection";
import {
  getPolicyFormProperty,
  setPolicyFormError,
  setPolicyFormProperty,
} from "../../../slices/policies";
import { useDispatch, useSelector } from "../../../store";
import { notEmpty } from "../../../validators/not-empty";
import PPLBuilder from "./PPLBuilder";

export type GeneralProps = {
  readonly: boolean;
  policyId: string;
  isCurrentTab: boolean;
};

export const GeneralPolicyConfiguration: FC<GeneralProps> = ({
  readonly,
  isCurrentTab,
}): ReactElement => {
  const enforced = useSelector(
    getPolicyFormProperty("enforced", false),
  ) as boolean;
  const dispatch = useDispatch();

  const onToggleEnforced = (): void => {
    dispatch(setPolicyFormProperty({ path: "enforced", value: !enforced }));
  };

  return (
    <Container disableGutters maxWidth={false}>
      <SettingSection label="Name" docsKey="policy-name">
        <ReduxTextField
          disabled={readonly}
          fullWidth
          path="name"
          setter={setPolicyFormProperty}
          getter={getPolicyFormProperty}
          errorHandler={setPolicyFormError}
          validator={notEmpty}
        />
      </SettingSection>
      <Divider />

      <SettingSection label="Policy Enforcement" docsKey="policy-enforcement">
        <RadioGroup
          aria-label="enforced"
          name="enforced"
          row
          value={enforced ? "true" : "false"}
          onChange={onToggleEnforced}
        >
          <Tooltip
            title={`This policy is enforced, meaning it automatically applies to any routes in this cluster.`}
            placement="top-start"
          >
            <FormControlLabel
              value="true"
              control={<Radio disabled={readonly} />}
              label="Enforced"
            />
          </Tooltip>
          <Tooltip
            title={`This policy is optional, meaning it is available to be applied to any of the routes in this cluster.`}
            placement="top-start"
          >
            <FormControlLabel
              value="false"
              control={<Radio disabled={readonly} />}
              label="Optional"
            />
          </Tooltip>
        </RadioGroup>
      </SettingSection>
      <Divider sx={{ mb: 2 }} />

      {isCurrentTab && <PPLBuilder readonly={readonly} />}
    </Container>
  );
};
export default GeneralPolicyConfiguration;
